/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 08-09-2026
 *
 * De opslagketen van een model (golf a3): wanneer er opgeslagen wordt, wat er in de undo-buffer
 * gaat, wat er in de actielog komt, welke stand daarbij hoort en welke changeId's er meegaan. Vijf
 * poten die in de AngularJS-code zo strak aan elkaar hingen dat elke knip een tweede waarheid
 * oplevert (kernbesluit 6): de actionbuffer reist mee in de save-payload, undo kloont de stand, en
 * de save leegt de changeId-lijst.
 *
 * De pure kant — de stand zelf en zijn undo-/redo-geschiedenis — staat in `Documentstand`, een
 * gewone klasse in eigendom van deze service. Hier staat alles wat een backendcall, een timer of een
 * guard nodig heeft.
 *
 * Afhankelijkheidsrichting: `DlModel ← OpslagService ← samenwerk-baas`. Deze service injecteert het
 * document, maar nooit een component: het canvas meldt zich als **tekenaar**, precies zoals de
 * luisteraars van `DlModel.bijGraafGetekend`. Wie op de opslag wil reageren, abonneert zich op de
 * meldingen hieronder.
 */
import {computed, inject, Injectable, signal, type Signal, type WritableSignal} from '@angular/core';
import type {NodeSingular} from 'cytoscape';
import {Sihwlog} from 'sihw-ngx';
import {elements} from 'dynalearn-common/lib/elements';
import {Melding} from '../../lib/melding';
import {TranslateService} from '@ngx-translate/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {DlModel} from './dl-model';
import {Documentstand} from './documentstand';
import type {ModelDocument, ModelMeta} from './model-serialisatie';
import {SamenwerkingService} from '../../services/samenwerking.service';
import type {SamenwerkRol} from '../../services/samenwerking.service';
import {SessieService} from '../../services/sessie.service';
import {FoutmeldingService} from '../../services/foutmelding.service';
import {ApiService, type ModelId} from '../backend/api.service';
import {NormService, type NormSnapshotblok, type WerkModelData} from '../engines/norm.service';
import {SimfeedbackEngineService} from '../engines/simfeedback-engine.service';
import {BouwsuggestieEngineService} from '../engines/bouwsuggestie-engine.service';
import type {BsSyncNode} from '../engines/bouwsuggestie-engine.service';
import {FeedbackSelectieService} from '../../services/feedback-selectie.service';
import type {BouwsuggestieSelectie, FoutSelectie} from '../../services/feedback-selectie.service';
import type {NormmatchElementen} from 'dynalearn-common/lib/normmatch.types';
import type {ChatRegel} from '../../services/chat.service';
import {BeginneruitlegDirective} from '../../directives/beginnersuitleg/beginnersuitleg.directive';
import {DlModelPropsComponent} from '../../components/dialogs/dl-model-props/dl-model-props.component';
import type {ModelPropsKeuze} from '../../components/dialogs/dl-model-props/model-props.types';

/**
 * Hoelang we na een wijziging wachten voordat er daadwerkelijk opgeslagen wordt. Komt er binnen die
 * tijd nog een wijziging, dan schuift de save op.
 */
const SAVE_VERTRAGING = 1500;

/**
 * Boven dit aantal elementen wordt een nog niet opgeslagen model niet meer stilzwijgend
 * overgeslagen: de gebruiker krijgt de nadrukkelijke ballon dat hij moet opslaan.
 */
const BALLONGRENS = 30;

/**
 * Hoe lang er minstens tussen twee snapshots van hetzelfde model zit. Dit is niet wat een gebaar tot
 * één stap samenvoegt — dat doet de rustvoorwaarde, want alles binnen één gebaar valt in één
 * rustvenster — maar de snelheidsbegrenzer voor als meer deelnemers tegelijk doorklikken. Gekozen op
 * productiecijfers (besluit 15-09-2026, 5 s: ruwweg 57% van de stappen blijft over) en op 16-09-2026
 * teruggezet naar 3 s (ruwweg 66%), omdat klikken in de simulatiegraaf anders stappen verloor.
 */
const SNAPSHOT_DREMPEL_MS = 3000;

/**
 * Waaróm de stand opnieuw getekend wordt. Vier gevallen, en elk vraagt er iets anders omheen:
 *
 * - **`nieuw model`** — een ánder model staat er nu: de viewport komt uit het document, en de
 *   normstate is leeg, dus de eerste uitslag komt in bulk binnen en mag niet animeren.
 * - **`herstel`** — een undo of redo van hetzelfde model. Wat de norm daarna vindt is niet nieuw
 *   maar teruggehaald: het icoon hoort er niet om te pulseren.
 * - **`samenwerk-herlading`** — dezelfde stand opnieuw, van de leider of na een herregistratie. De
 *   vorige normuitslag blijft staan, en wat er verandert is wél nieuw voor deze gebruiker.
 * - **`replaystap`** — een stap in de teacher-replay (golf a5).
 */
export type Tekenaanleiding = 'nieuw model' | 'herstel' | 'samenwerk-herlading' | 'replaystap';

/**
 * Wat de opslagketen van het canvas vraagt: het document tekenen en er een voorbeeldplaatje van
 * maken. Het canvas-eiland meldt zich hiervoor aan met `registreerTekenaar`, zodat deze service
 * nooit een component hoeft te injecteren.
 */
export interface Modeltekenaar {
    /**
     * Tekent het document op het canvas. Mag een promise teruggeven — het loslaten van de
     * animatieblokkade wacht op de bouwsuggestie-run — maar tekent synchroon: als de aanroep
     * terugkeert staat de graaf er.
     * @param doc het document in het opslagformaat
     * @param aanleiding waarom er getekend wordt
     */
    herlaad(doc: ModelDocument, aanleiding: Tekenaanleiding): void | Promise<void>;

    /**
     * Het voorbeeldplaatje van het model zoals het op het canvas staat, als base64-string.
     */
    jpg(maxwidth?: number, maxheight?: number): string;
}

/**
 * De undostatus zoals hij tussen leider en volgers reist.
 */
export interface Undostatus {
    canUndo: boolean;
    canRedo: boolean;
}

/**
 * Eén regel in de actielog, zoals hij in de buffer wacht tot de eerstvolgende save hem meestuurt.
 * De veldnamen zijn het contract met het backend (`controller/model.js`, `save`) en liggen vast.
 */
export interface Actielogregel {
    action: string;
    target?: string | null;
    volger?: string | null;
    targettype?: string | null;
    arguments: Record<string, unknown>;
    timestamp: number;
}

/**
 * Een vastgelegde maar nog niet geschreven actielogregel.
 *
 * Waarom die tussenvorm bestaat: het moment waarop je de *inhoud* van een regel kunt aflezen en het
 * moment waarop de regel *geschreven* hoort te worden vallen niet samen. Bij een delete zijn de
 * route en de argumenten van het element ná de mutatie weg, dus die moet je vóóraf aflezen; maar de
 * regel zelf hoort ná de actie te staan, net als de snapshot die eraan hangt (besluit gebruiker
 * 08-09-2026). Een uitvoerder legt zijn regels dus vast op het moment dat hij ze kan aflezen, en de
 * samenwerk-baas schrijft ze in één keer nadat de mutatie klaar is.
 */
export interface Logintentie {
    action: string;
    target: string | null;
    targettype: string | null;
    args: Record<string, unknown>;
    volger: string | null;
    /** Of het schrijven van deze regel als inhoudelijke wijziging gemeld wordt. */
    meldInhoudGewijzigd: boolean;
}

/**
 * Wat het backend bij het ophalen van een model teruggeeft. De veldnamen zijn het protocol
 * (`controller/model.js`, `get`) en liggen daarmee vast.
 */
export interface Laadresultaat {
    id: ModelId;
    titel: string;
    /** Het opslagformaat als json-string. */
    json: string;
    versie: number;
    modelleerniveau?: number;
    simulatiepreferentie?: number;
    fastest_path?: boolean | null;
    /** Een gebruikersnaam als het model van iemand anders is. */
    userprefix?: string;
    /** De rol in een samenwerking: `single`, `primary` of `sub`. */
    samenwerking?: SamenwerkRol;
    /** Het primarymodel, als dit een sub is. */
    primary?: {id: ModelId};
    /** Staat dit model open om mee te kijken? */
    meekijkmodel?: ModelId | null;
    /** De chathistorie van dit model. */
    chat?: ChatRegel[];
    /** Het hoogste volgnummer dat er voor dit model ligt; het vertrekpunt van de snapshotteller. */
    snapshotvolgorde?: number;
    /** Hoe lang geleden de laatste snapshot van dit model geschreven is, in ms; `null` als er geen is. */
    snapshotleeftijd?: number | null;

    [veld: string]: unknown;
}

/**
 * Wat er meegaat als er een model geladen is: alles wat een luisteraar niet zelf uit de service kan
 * lezen. De chat krijgt zijn historie zo binnen.
 */
export interface Geladenmelding {
    chat: ChatRegel[];
}

/**
 * Hoe de opslagketen een model bij de samenwerking aan- en afmeldt. Een registratie en geen
 * injectie: de samenwerkregistratie is protocol en woont in de samenwerk-baas, en die injecteert
 * deze service al — andersom zou een cyclus geven.
 */
export type Samenwerkhaak = (deregistreer?: boolean) => Promise<unknown> | unknown;

/**
 * De simulatiestand zoals hij in een snapshot meegaat. Wordt aangeleverd door de geregistreerde
 * snapshotbron: het samenstellen ervan vraagt het simcanvas, de simulatiestatus en de
 * simfeedback-engine, en die kent deze service niet.
 */
export interface Simulatiestand {
    simstatus: unknown;
    /**
     * De invoerhash van de simulatie in beeld, of `null`. Gaat los mee naar het backend, dat hem in een
     * eigen kolom zet: zo ziet het opruimen welke bewaarde simulaties een snapshot nog nodig heeft.
     */
    simhash: string | null;
}

/**
 * Een snapshot: een screenshot van een rusttoestand van het model. Het opslagformaat, de
 * simulatiestand, de feedbackstand en de aanwijzing, alle vier van hetzelfde moment. Dit is wat de
 * replay terugtekent.
 *
 * De drie feedbackvelden zijn optioneel omdat snapshots van vóór golf a5 ze niet hebben; de replay dimt
 * zo'n stap.
 *
 * Zonder de zoom (`cy`): de replay laat de uitsnede staan, en de zoom verschilt per client en na elke
 * herlading. Snapshots van vóór 16-09-2026 hebben hem nog wel.
 */
export interface Modelsnapshot extends Omit<ModelDocument, 'cy'>, Partial<Simulatiestand> {
    /** De normstand: het foutmodel en de tellingen mét de verwachting. */
    norm?: NormSnapshotblok;
    /** De bouwsuggestie-run: dezelfde vorm als het `bsSync`-bericht. */
    bouwsuggesties?: BsSyncNode[];
    /** Wat er aangewezen stond. De simulatieselectie reist al mee in `simstatus.simfeedback`. */
    selectie?: {fout: FoutSelectie | null; bs: BouwsuggestieSelectie | null};
}

/**
 * De metaproperties van een model die los van de graaf gezet kunnen worden. Alleen wat er
 * meegegeven wordt, verandert.
 */
export interface Modelproperties {
    titel?: string;
    modelleerniveau?: number;
    simulatiepreferentie?: number;
    fastest_path?: boolean | null;
}

/**
 * Wat er bij een propertywijziging gemeld wordt: welke properties er daadwerkelijk gezet zijn, en
 * het meegegeven vrije argument waarmee de aanroeper zijn eigen flow herkent.
 */
export interface Propswijziging {
    gewijzigd: Modelproperties;
    notifyArg?: unknown;
}

@Injectable({providedIn: 'root'})
export class OpslagService {

    private readonly log = new Sihwlog('info', 'OpslagService');

    /**
     * Het document: de graaf, de scalars en de readonly-stand waar de guards hieronder op leunen.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De samenwerk-sessie-state: een volger slaat niet op en ondo't niet zelf.
     */
    private readonly samenwerking = inject(SamenwerkingService);

    /**
     * Het backend: opslaan, het voorbeeldplaatje en straks de actielog.
     */
    private readonly api = inject(ApiService);

    /**
     * De sessie: de projectinstellingen achter het effectieve modelleerniveau en de
     * eigenschappendialoog.
     */
    private readonly sessie = inject(SessieService);

    /**
     * Het faalpad van het opslaan: een vertaalde melding aan de gebruiker.
     */
    private readonly foutmelding = inject(FoutmeldingService);

    /**
     * De vertaling van de standaardtitel van een nieuw model.
     */
    private readonly translate = inject(TranslateService);

    /**
     * Voor de eigenschappendialoog.
     */
    private readonly modalService = inject(BsModalService);

    /**
     * De norm: wij triggeren zijn telling en zijn match, en luisteren op wat hij daarna meldt.
     * Andersom injecteren zou een cyclus geven — vandaar de meldingen.
     */
    private readonly normService = inject(NormService);

    /**
     * De simfeedback-engine: hij rekent, wij leggen zijn uitslag in de actielog vast en zetten bij
     * een ander model zijn ijklijn voor "wat is hier nieuw" terug.
     */
    private readonly simfeedbackEngine = inject(SimfeedbackEngineService);

    /**
     * De bouwsuggestie-engine: bij een nieuw model zetten wij zijn ijklijn voor "wat is hier nieuw"
     * terug, net als bij de norm. Het draaien zelf doet de samenwerk-baas.
     */
    private readonly bouwsuggestieEngine = inject(BouwsuggestieEngineService);

    /**
     * De feedbackselectie: wat er aangewezen stond hoort bij de stand en gaat dus mee in de snapshot.
     */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    constructor() {
        this.luisterNaarDeNorm();
        //de `simfb`-regel na een run met resultaat; hier zitten het modelid en de snapshots
        this.simfeedbackEngine.registreerLogSimfb(feedback => {
            this.directlogAction('simfb', feedback as unknown as Record<string, unknown>, 'model', 'model');
            this.markeerSnapshotNodig();
        });
        //het einde van een bouwsuggestie-run is een van de momenten om naar rust te kijken
        this.bouwsuggestieEngine.bijRunKlaar(() => this.planRustcheck(0));
    }

    /**
     * De stand en zijn geschiedenis.
     */
    private readonly stand = new Documentstand();

    /**
     * Het canvas dat het document tekent en het voorbeeldplaatje levert; `null` zolang het eiland
     * zich niet gemeld heeft.
     */
    private tekenaar: Modeltekenaar | null = null;

    /**
     * Of de stand een undostap terug kan. Spiegel van `Documentstand`, die geen signal is; wordt na
     * elke stapelwijziging bijgewerkt.
     */
    private readonly _standCanUndo: WritableSignal<boolean> = signal(false);

    /**
     * Of de stand een undostap vooruit kan; zie `_standCanUndo`.
     */
    private readonly _standCanRedo: WritableSignal<boolean> = signal(false);

    /**
     * De laatst gemelde undostatus van de leider. Een volger heeft geen eigen stapel: hij toont de
     * knoppen van de leider en stuurt zijn undo als verzoek door. Komt binnen via
     * `zetLeiderUndostatus`, vanuit de samenwerk-baas.
     */
    private readonly _leiderUndostatus: WritableSignal<Undostatus> = signal({canUndo: false, canRedo: false});

    /**
     * Het aantal redenen om het opslaan tegen te houden; zie `blokkeerOpslag`.
     */
    private opslaglock = 0;

    /**
     * Het id van het opgeslagen model, of `null` zolang het nog nooit is opgeslagen.
     */
    private readonly _modelId: WritableSignal<ModelId | null> = signal(null);

    /**
     * Het model waarvan we notificaties willen: bij een sub het primary-model, anders het model
     * zelf. Ook de chat hangt hieraan.
     */
    private readonly _notifyId: WritableSignal<ModelId | null> = signal(null);

    /**
     * De gebruikersnaam vóór de modeltitel, als het model van iemand anders is.
     */
    private readonly _userprefix: WritableSignal<string> = signal('');

    /**
     * De opslagversie zoals het backend hem het laatst teruggaf.
     */
    private readonly _versie: WritableSignal<number> = signal(0);

    /**
     * De changeId's die we als leider hebben rondgestuurd sinds de vorige save, of als volger
     * hebben ontvangen sinds de vorige `modelchanged`. Een gewone array: de lezer is de
     * changeId-controle van de samenwerk-baas (`opModelchanged`), die er alleen in zoekt.
     */
    private samenwerkchanges: string[] = [];

    /**
     * De vertaalde standaardtitel van een nieuw model, zoals `reset()` hem zet. De save vergelijkt
     * de titel ermee om het backend om een unieke naam te vragen. Bewust bij elke reset opnieuw
     * vertaald en niet bij constructie: dan kan de vertaallader nog bezig zijn en is de "vertaling"
     * de sleutel zelf.
     */
    private standaardtitel = '';

    /**
     * De modelgeneratie waar we mee bezig zijn. Loopt op bij elke modelwissel, zodat een save die
     * onderweg is zichzelf herkent als achterhaald.
     */
    private generatie = 1;

    /**
     * De generatie waarvoor het opslaan op dit moment loopt, of 0 als er niet opgeslagen wordt.
     */
    private saveGeneratie = 0;

    /**
     * Loopt zolang er een save onderweg is; zie `onNotSaving`.
     */
    private saveBezig: Promise<boolean> | null = null;

    /**
     * De timer van een geplande save.
     */
    private savePlanner: ReturnType<typeof setTimeout> | null = null;

    /**
     * Of er nog een save gepland staat die niet vertrokken is. De replay leest hem: staat er niets in
     * de buffer én is er niets gepland, dan valt er vóór het terugbladeren niets weg te schrijven.
     */
    public get saveGepland(): boolean {
        return this.savePlanner !== null;
    }

    /**
     * Of er een snapshot nodig is: een actie heeft iets in beweging gebracht, en sindsdien is er nog
     * geen snapshot gemaakt. Zie `markeerSnapshotNodig`.
     */
    private snapshotNodig = false;

    /**
     * Wanneer de laatste snapshot van dit model geschreven is, als epoch in ms. Van het model en niet
     * van deze client: hij komt mee bij het laden, uit elke save, uit een moduswijziging en uit elk
     * samenwerkbericht, zodat een nieuwe leider niet meteen een snapshot schiet.
     */
    private laatsteSnapshotmoment = 0;

    /**
     * De geplande rustcheck; zie `planRustcheck`.
     */
    private rustcheck: ReturnType<typeof setTimeout> | null = null;

    /**
     * Het aantal snapshots dat nog onderweg is naar het backend. `wachtOpSnapshots` wacht erop.
     */
    private snapshotsOnderweg = 0;

    /**
     * De laatste snapshot die wij van dit model schreven: zijn json met de weergavestand erachter, en de
     * stand van de teller direct daarna. Is de volgende gelijk en heeft niemand intussen een hoger
     * volgnummer gemeld, dan ligt die stand al als laatste stap in het backend en hoeft hij niet nog eens
     * over de lijn. Bij een groot model scheelt dat een flinke reis voor niets, bijvoorbeeld bij het nog
     * eens aanwijzen van wat er al aangewezen stond.
     */
    private laatsteSnapshot: {modelId: ModelId; inhoud: string; teller: number} | null = null;

    /**
     * Of de ballon "je moet nog opslaan" al getoond is; hij komt maar één keer per model.
     */
    private opslaanballonGetoond = false;

    /**
     * De actielogregels die op de eerstvolgende save wachten. Ze reizen mee in de save-payload en
     * worden pas geleegd als die geslaagd is.
     */
    private actielogbuffer: Actielogregel[] = [];

    /**
     * Waar de simulatiestand voor een snapshot vandaan komt; `null` zolang niemand zich gemeld
     * heeft. Zie `registreerSnapshotbron`.
     */
    private snapshotbron: (() => Simulatiestand) | null = null;

    /**
     * Hoe we een model bij de samenwerking aan- en afmelden; `null` zolang de samenwerk-baas zich
     * niet gemeld heeft. Zie `registreerSamenwerkhaak`.
     */
    private samenwerkhaak: Samenwerkhaak | null = null;

    /**
     * De fouttelling zoals we die het laatst in de actielog zetten. Vergelijkingsmateriaal, zodat
     * dezelfde telling niet elke ronde opnieuw een regel oplevert.
     */
    private laatstGelogdeTelling: unknown = {};

    /**
     * De stand is vervangen en moet opnieuw getekend worden (ng1: `model.changed`).
     */
    private readonly herladenMelding = new Melding<Tekenaanleiding>();

    /**
     * Er is een nieuw, leeg model begonnen (ng1: `model.reset`).
     */
    private readonly geresetMelding = new Melding<void>();

    /**
     * Onze undostatus is gewijzigd; de samenwerking stuurt hem naar de volgers.
     */
    private readonly undostatusMelding = new Melding<Undostatus>();

    /**
     * De metaproperties van het model zijn gezet (ng1: `model.propsChanged`).
     */
    private readonly propsGewijzigdMelding = new Melding<Propswijziging>();

    /**
     * Het model is voor het eerst opgeslagen en heeft nu een id. Luisteraars mogen een promise
     * teruggeven: de save wacht erop voordat hij het voorbeeldplaatje verstuurt.
     */
    private readonly eersteOpslagMelding = new Melding<void>();

    /**
     * Er is een inhoudelijke wijziging gelogd (ng1: `model.contentChanged`).
     */
    private readonly inhoudGewijzigdMelding = new Melding<void>();

    /**
     * Er is een model geladen, met wat daarbij hoort (de chathistorie).
     */
    private readonly geladenMelding = new Melding<Geladenmelding>();

    /* ***************** DE STAND ***************** */

    /**
     * De huidige stand, als losse kopie.
     */
    public data(): ModelDocument {
        return this.stand.data();
    }

    /**
     * De huidige stand zonder kopie, voor lezers die er niets in schrijven en er niets van bewaren
     * (de normtelling en de normmatch).
     */
    public ruweData(): ModelDocument {
        return this.stand.ruweData();
    }

    /**
     * Vervangt de stand door een kopie van het gegeven document. Zonder melding: dit is de weg van
     * het opslaan, waar wat er op het canvas staat al de nieuwe stand ís.
     */
    public zetStand(doc: ModelDocument): void {
        this.stand.zet(doc);
    }

    /**
     * Vervangt de stand door een document dat net uit json is uitgepakt, zonder kopie. Zonder
     * melding: de laadketen meldt zelf wanneer het beeld bij moet zijn.
     */
    public neemStandOver(doc: ModelDocument): void {
        this.stand.neemOver(doc);
    }

    /**
     * Zet onze vaste metadata op de huidige stand. Die hoort niet bij wat de serialisatie schrijft
     * (een snapshot heeft hem niet), maar wel bij elk opgeslagen model.
     */
    public addMeta(): void {
        this.stand.addMeta(this.huidigeMeta());
    }

    /**
     * Begint opnieuw met een leeg model: het document, de identiteit, de samenwerkstand en de norm
     * gaan terug naar hun beginwaarde, en dat wordt gemeld. Het vertrekpunt van elke `nieuw…`- en
     * `laad`-route.
     * @param [behoudUndobuffer] laat de undo-geschiedenis staan (na een herlading van hetzelfde model)
     */
    public reset(behoudUndobuffer?: boolean): void {
        this.generatie++; //oude saves zijn ongeldig op dit model
        //de document-scalars; `titel` doet daar bewust niet aan mee, want die hoort bij de
        //standaardtitel hieronder
        this.dlModel.reset();
        //bij elke reset opnieuw vertalen: bij constructie kan de vertaallader nog bezig zijn
        this.standaardtitel = this.translate.instant('NEWMODEL');
        this.dlModel.titel = this.standaardtitel;
        this.bepaalModelleerNiveau(); //die leest de projectinstellingen
        this._modelId.set(null);
        this._notifyId.set(null);
        this._userprefix.set('');
        this._versie.set(0);
        this.samenwerkchanges = [];
        this.opslaanballonGetoond = false;
        //en de ballon zelf ook weg, niet alleen de vlag. Hij staat vier seconden in beeld, en wie een
        //ander model opent bewaart eerst — dan verschijnt de waarschuwing "sla je model onder een goede
        //naam op" bij het vórige model, maar staat hij er nog als het nieuwe al getekend is (bevinding
        //23-09-2026, test 20 van golf 4e, op beide ingangen)
        BeginneruitlegDirective.sluitAlle();
        this.annuleerGeplandeSave();
        this.leegActionbuffer();
        //een snapshot die nog nodig was hoort bij het vorige model. De teller en het moment zijn van
        //het model; het laden zet ze op de stand van dát model
        this.snapshotNodig = false;
        if (this.rustcheck) {
            clearTimeout(this.rustcheck);
            this.rustcheck = null;
        }
        this.snapshotteller = 0;
        this.laatsteSnapshotmoment = 0;
        this.laatsteSnapshot = null;
        this.laatstGelogdeTelling = {}; //ander model: de vorige gelogde telling zegt niets meer
        //de sessie hoort bij het model: een nieuw model werkt niet samen en is niet geblokkeerd
        this.samenwerking.zetRolUitBackend('single');
        this.samenwerking.zetWerkmodus('lokaal');
        this.samenwerking.zetAanwezigen([]);
        this.dlModel.laatBewerkenLos();
        this.zetLeiderUndostatus({canUndo: false, canRedo: false}); //we weten nog niet wat de leider kan
        //de normstate (interpretaties, telling, naamtelling, generatiebewaking, lopende worker), de
        //bouwsuggestie-state en de ijklijn van de simfeedback: alle drie zetten ze hun vertrekpunt
        //voor "wat is hier nieuw" terug op "geen voorganger". Zonder dat vergelijkt een net geladen
        //model met de uitslag van het vórige. De simfeedback houdt zijn ijklijn juist wél vast als
        //een simulatie sluit — daar is de vraag wat er nieuw is sinds de vorige simulatieronde
        this.normService.reset();
        this.bouwsuggestieEngine.reset();
        this.simfeedbackEngine.wisIjklijn();
        this.stand.reset();
        this.addMeta();
        if (!behoudUndobuffer) {
            this.log.debug('[undo] Reset');
            this.wisGeschiedenis();
        }
        this.geresetMelding.meld();
    }

    /**
     * Zet een expliciet aangeleverde stand neer — bij samenwerking, of vanuit een undo bij de
     * leider — en laat hem tekenen. Dezelfde weg als een undo: hetzelfde model, een andere stand.
     */
    public setData(doc: ModelDocument): void {
        if (this.dlModel.fullReadonly) {
            this.log.debug('setData: geskipt wegens fullReadonly');
            return;
        }
        this.stand.zet(doc);
        this.addMeta();
        this.herlaad('samenwerk-herlading');
        this.verwerkNormstand();
    }

    /**
     * De vaste metadata bij de huidige stand.
     */
    private huidigeMeta(): ModelMeta {
        return {
            id: this._modelId(),
            name: this.dlModel.titel,
            norm: this.dlModel.norm,
            notationversion: 1 //welke notatie gebruiken we in dit model
        };
    }

    /**
     * Laat de huidige stand opnieuw tekenen en meldt de herlading. De melding gaat vóór het
     * tekenen, precies zoals de ng1-broadcast dat deed: de luisteraars breken hun replay af en
     * plannen hun eigen bijwerking, en dan staat de graaf er nog binnen dezelfde tick.
     * @param aanleiding waarom er getekend wordt
     */
    public herlaad(aanleiding: Tekenaanleiding): void | Promise<void> {
        this.herladenMelding.meld(aanleiding);
        return this.tekenaar?.herlaad(this.stand.data(), aanleiding);
    }

    /* ***************** DE IDENTITEIT VAN HET MODEL ***************** */

    /**
     * Het id van het opgeslagen model, of `null` zolang het nog nooit is opgeslagen.
     */
    public readonly modelId: Signal<ModelId | null> = this._modelId.asReadonly();

    /**
     * Of we met een opgeslagen model werken.
     */
    public readonly isOpgeslagen: Signal<boolean> = computed(() => !!this._modelId());

    /**
     * Het model waarvan we notificaties willen: bij een sub het primary-model, anders het model
     * zelf.
     */
    public readonly notifyId: Signal<ModelId | null> = this._notifyId.asReadonly();

    /**
     * De gebruikersnaam vóór de modeltitel, als het model van iemand anders is.
     */
    public readonly userprefix: Signal<string> = this._userprefix.asReadonly();

    /**
     * De opslagversie zoals het backend hem het laatst teruggaf.
     */
    public readonly versie: Signal<number> = this._versie.asReadonly();

    /**
     * Zet het model-id. Wordt door de laadketen gezet en door de save als een nieuw model voor het
     * eerst een id krijgt.
     */
    public zetModelId(id: ModelId | null): void {
        this._modelId.set(id ?? null);
    }

    /**
     * Zet het model waarvan we notificaties willen.
     */
    public zetNotifyId(id: ModelId | null): void {
        this._notifyId.set(id ?? null);
    }

    /**
     * Zet het voorvoegsel voor de modeltitel.
     */
    public zetUserprefix(prefix: string): void {
        this._userprefix.set(prefix || '');
    }

    /**
     * Zet de opslagversie.
     */
    public zetVersie(versie: number): void {
        this._versie.set(versie || 0);
    }

    /**
     * De changeId's die nog in de eerstvolgende save meegaan, of waarop een volger controleert of
     * hij bij is.
     */
    public changes(): string[] {
        return this.samenwerkchanges;
    }

    /**
     * Voegt een changeId toe aan de lijst met verwerkte changes.
     */
    public samenwerkchange(changeId: string): void {
        this.samenwerkchanges.push(changeId);
    }

    /**
     * Leegt de lijst met verwerkte changes.
     */
    public resetSamenwerkchanges(): void {
        this.samenwerkchanges = [];
    }

    /**
     * Verklaart lopende saves ongeldig, omdat we op een ander model overgaan. Aangeroepen door
     * `herlaadUitBackend` en `ontkoppel`.
     */
    public nieuweGeneratie(): void {
        this.generatie++;
    }

    /* ***************** DE ACTIELOGBUFFER ***************** */

    /**
     * De regels die op de eerstvolgende save wachten, als losse kopie.
     */
    public actionbuffer(): Actielogregel[] {
        return [...this.actielogbuffer];
    }

    /**
     * Gooit de buffer weg omdat hij niet meer bij de stand klopt: een reset, of een herlading van
     * hetzelfde model. (Een geslaagde save haalt er alleen zijn eigen voorste stuk uit; zie
     * `saveModel`.)
     */
    public leegActionbuffer(): void {
        this.actielogbuffer = [];
    }

    /**
     * Zet een actie in de buffer, om met het model mee opgeslagen te worden. Geeft de regel terug, of
     * `null` in readonly.
     * @param action de actienaam
     * @param target de beschrijving van waar de actie op sloeg, meestal `DlModel.route(node)`
     * @param volger bij samenwerking het clientid van de volger, zodat het backend de juiste
     *   gebruiker eraan koppelt
     * @param targettype het concrete elementtype, voor de statistiek
     * @param args de relevante argumenten bij de actie
     * @param [meldInhoudGewijzigd] of dit als inhoudelijke wijziging gemeld wordt (standaard wel)
     */
    public logaction(
        action: string, target: string | null, volger: string | null, targettype: string | null,
        args?: Record<string, unknown>, meldInhoudGewijzigd = true
    ): Actielogregel | null {
        if (this.dlModel.fullReadonly) {
            this.log.debug('logaction: geskipt wegens fullReadonly');
            return null;
        }
        const regel: Actielogregel = {
            action,
            target: target ?? null,
            volger: volger ?? null,
            targettype: targettype ?? null,
            arguments: args || {},
            timestamp: Date.now()
        };
        this.actielogbuffer.push(regel);
        if (meldInhoudGewijzigd) {
            this.inhoudGewijzigdMelding.meld();
        }
        return regel;
    }

    /**
     * Logt een actie los van het opslaan. Bedoeld voor gebaren die wel in de actielog horen maar
     * bij de leider niets veranderen — het openen van een simulatie, het selecteren van een
     * toestand. Bij een nog niet opgeslagen model gaat de regel alsnog de buffer in.
     * @param action de actienaam
     * @param [actionArgs] de argumenten bij de actie
     * @param [target] de beschrijving van waar de actie op sloeg
     * @param [targettype] het concrete elementtype
     * @param [logNoModel] ook loggen als er geen opgeslagen model is (dan met `model = null`)
     * @param [volger] het clientid van de volger die de actie startte
     */
    public directlogAction(
        action: string, actionArgs?: Record<string, unknown>, target?: string, targettype?: string,
        logNoModel?: boolean, volger?: string | null
    ): Actielogregel | Promise<{id: number}> | null {
        if (this.dlModel.fullReadonly) {
            this.log.debug('directlogAction: geskipt wegens fullReadonly');
            return null;
        }
        if (this._modelId() || logNoModel) {
            return this.api.logAction(this._modelId(), action, actionArgs, target, targettype, volger);
        }
        this.log.debug('Directe actielog in een niet-opgeslagen model: bewaren in de buffer');
        return this.logaction(action, target ?? null, volger ?? null, targettype ?? null, actionArgs, false);
    }

    /* ***************** DE SNAPSHOTS ***************** */

    /**
     * Meldt dat er een snapshot nodig is: een actie heeft iets in beweging gebracht. Hier wordt niets
     * vastgelegd. De snapshot volgt zodra het model tot rust gekomen is (`probeerSnapshot`) en is dan
     * een screenshot van die rusttoestand, niet de uitkomst van deze ene actie. Komen er vóór dat
     * moment nog meer acties langs, dan levert dat samen één snapshot op.
     *
     * Alleen een leider of een lokale gebruiker zet de vlag; een volger schrijft geen snapshots. In
     * `fullReadonly` evenmin: wie het readonly beëindigt, meldt zelf dat er weer een nodig is.
     */
    public markeerSnapshotNodig(): void {
        if (!this.sessie.actionlogSnapshots() || this.dlModel.fullReadonly || this.samenwerking.isVolger()) {
            return;
        }
        this.snapshotNodig = true;
        this.planRustcheck(0);
    }

    /**
     * Wacht tot er geen snapshot meer nodig is en er ook geen meer onderweg is naar het backend, of tot
     * de limiet verstrijkt. Geeft terug of alles op tijd klaar was.
     *
     * De replay gebruikt dit vlak voordat hij zijn stappenlijst ophaalt: staat de snapshot van je
     * laatste actie nog uit, dan mist die stap in de lijst die hij terugkrijgt. Het wachten kan tot de
     * drempel duren, als de vorige snapshot net geschreven is.
     * @param limietMs hoe lang we er hoogstens op wachten
     */
    public async wachtOpSnapshots(limietMs: number): Promise<boolean> {
        const eind = Date.now() + limietMs;
        while (this.snapshotNodig || this.snapshotsOnderweg > 0) {
            if (Date.now() >= eind) {
                return false;
            }
            await new Promise(klaar => setTimeout(klaar, 100));
        }
        return true;
    }

    /**
     * Plant een rustcheck. Altijd in een eigen taak, ook zonder vertraging: de signalen komen midden in
     * een keten binnen — een actie die zijn opslagronde nog moet starten, een bouwsuggestie-run waarvan
     * de save pas in de microtaken erna volgt — en pas als die keten uitgelopen is, is te zien of er nog
     * iets rekent. Een nieuwe planning vervangt de vorige; de check rekent de resterende wachttijd zelf
     * opnieuw uit.
     * @param vertraging na hoeveel ms er gekeken wordt
     */
    private planRustcheck(vertraging: number): void {
        if (this.rustcheck) {
            clearTimeout(this.rustcheck);
        }
        this.rustcheck = setTimeout(() => {
            this.rustcheck = null;
            this.probeerSnapshot();
        }, vertraging);
    }

    /**
     * Maakt de snapshot als het model tot rust gekomen is. Rust is: de vlag staat, er rekent geen
     * normworker, er is geen bouwsuggestie-run gepland of bezig, en de vorige snapshot van dit model is
     * langer dan `SNAPSHOT_DREMPEL_MS` geleden. Nagegaan op vier momenten: bij het zetten van de vlag,
     * bij elke melding over een normronde, aan het eind van elke bouwsuggestie-run en als de
     * wachtdrempel verloopt.
     *
     * Een afgebroken normronde telt als rust met wat er ligt. Start er meteen een nieuwe, dan meldt de
     * norm die afbreking pas als de nieuwe al rekent.
     */
    private probeerSnapshot(): void {
        if (!this.snapshotNodig || this.dlModel.fullReadonly) {
            return; //in readonly kan een staande vlag niets; wie het readonly beëindigt, zet hem opnieuw
        }
        if (this.samenwerking.isVolger()) {
            this.snapshotNodig = false; //intussen volger geworden: de snapshots zijn aan de leider
            return;
        }
        if (this.normService.rekent || this.bouwsuggestieEngine.runGepland) {
            return; //het signaal van die ronde of run komt nog
        }
        const wachttijd = this.laatsteSnapshotmoment + SNAPSHOT_DREMPEL_MS - Date.now();
        if (wachttijd > 0) {
            this.planRustcheck(wachttijd);
            return;
        }
        void this.maakSnapshot();
    }

    /**
     * Logt het maken van een node.
     * @param node de zojuist gemaakte node
     * @param [args] relevante argumenten bij de actie
     * @param [volger] het clientid van de volger die de actie startte
     */
    public logcreate(node: NodeSingular, args?: Record<string, unknown>, volger?: string | null): Actielogregel | null {
        return this.logaction('create', this.dlModel.route(node), volger ?? null,
            node.data('type') as string, this.elementargumenten(node, args));
    }

    /**
     * Logt het verwijderen van een node. Alleen de hoofdnode wordt gelogd, en de aanroeper doet dat
     * vóór de mutatie: daarna zijn de route en de argumenten er niet meer.
     */
    public logdelete(node: NodeSingular, args?: Record<string, unknown>, volger?: string | null): Actielogregel | null {
        return this.logaction('delete', this.dlModel.route(node), volger ?? null,
            node.data('type') as string, this.elementargumenten(node, args));
    }

    /**
     * Logt een wijziging aan een node; wát er gewijzigd is staat in `args`.
     */
    public logmodify(node: NodeSingular, args?: Record<string, unknown>, volger?: string | null): Actielogregel | null {
        return this.logaction('modify', this.dlModel.route(node), volger ?? null,
            node.data('type') as string, this.elementargumenten(node, args));
    }

    /**
     * Legt een elementregel (`create`/`delete`/`modify`) vast zonder hem te schrijven: de route en
     * de argumenten worden nú afgelezen, het schrijven gebeurt later met `schrijfLog`. Zie
     * `Logintentie` voor waarom die twee uit elkaar liggen.
     */
    public legElementLogVast(action: 'create' | 'delete' | 'modify', node: NodeSingular,
                             args?: Record<string, unknown>, volger?: string | null): Logintentie {
        return {
            action,
            target: this.dlModel.route(node),
            targettype: node.data('type') as string,
            args: this.elementargumenten(node, args),
            volger: volger ?? null,
            meldInhoudGewijzigd: true
        };
    }

    /**
     * Legt een vrije actielogregel vast zonder hem te schrijven.
     */
    public legLogVast(action: string, target: string | null, targettype: string | null,
                      args?: Record<string, unknown>, volger?: string | null,
                      meldInhoudGewijzigd = true): Logintentie {
        return {action, target, targettype, args: args ?? {}, volger: volger ?? null, meldInhoudGewijzigd};
    }

    /**
     * Schrijft een eerder vastgelegde regel in de buffer.
     */
    public schrijfLog(intentie: Logintentie): Actielogregel | null {
        return this.logaction(intentie.action, intentie.target, intentie.volger, intentie.targettype,
            intentie.args, intentie.meldInhoudGewijzigd);
    }

    /**
     * Neemt over hoe lang geleden de laatste snapshot van dit model geschreven is, zoals een ander het
     * meldt: het backend bij het laden en na een save, de leider in elk samenwerkbericht, de verdeler
     * bij een moduswijziging. Het wordt teruggerekend naar een moment op onze eigen klok, zodat een
     * klokverschil met die ander er niet in doorwerkt. Nooit terug: een eerder moment is oud nieuws.
     * @param leeftijd in ms; `null` of afwezig als er nog geen snapshot is
     */
    public zetSnapshotleeftijd(leeftijd: number | null | undefined): void {
        if (leeftijd === null || leeftijd === undefined) {
            return;
        }
        this.laatsteSnapshotmoment = Math.max(this.laatsteSnapshotmoment, Date.now() - leeftijd);
    }

    /**
     * Hoe lang geleden de laatste snapshot van dit model geschreven is, zoals wij het kennen, in ms;
     * `null` als we er geen kennen. Reist over de samenwerklijn mee, net als het volgnummer.
     */
    public get snapshotleeftijd(): number | null {
        return this.laatsteSnapshotmoment ? Date.now() - this.laatsteSnapshotmoment : null;
    }

    /**
     * De stand van de snapshotteller: het hoogste volgnummer dat wij voor dit model kennen. Elke
     * snapshot claimt het nummer daarboven, en dat nummer bepaalt zijn plek in de replay.
     *
     * De teller is van het model en niet van deze client: hij wordt geseed bij het laden, bijgetrokken
     * uit elke save, uit een moduswijziging en uit elk samenwerkbericht. Wie de leiding overneemt telt
     * daardoor verder boven wat er al ligt in plaats van vanaf zijn eigen stand (besluit 15-09-2026).
     */
    private snapshotteller = 0;

    /**
     * Trekt de teller bij tot wat een ander ons meldt. Nooit omlaag: een lagere opgave is oud nieuws.
     * @param hoogste het hoogste volgnummer dat de ander kent
     */
    public zetSnapshotteller(hoogste: number | null | undefined): void {
        this.snapshotteller = Math.max(this.snapshotteller, Number(hoogste) || 0);
    }

    /**
     * Het nummer dat de eerstvolgende snapshot gaat claimen. Dit is wat er over de samenwerklijn
     * meereist: een volger die het hoort weet daarmee waar hij moet beginnen als hij de leiding krijgt,
     * ook als de snapshot van de laatste actie nog niet geschreven is.
     */
    public get volgendeSnapshotvolgorde(): number {
        return this.snapshotteller + 1;
    }

    /**
     * Meldt waar de simulatiestand voor een snapshot vandaan komt. Een registratie en geen
     * injectie: het samenstellen ervan vraagt het simcanvas, de simulatiestatus en de
     * simfeedback-engine, en dat is precies de kennis die deze service niet hoort te hebben. De
     * samenwerk-baas meldt hem aan; eigenlijk hoort hij bij de `SimulatieService`, maar die
     * verhuizing staat nog open.
     */
    public registreerSnapshotbron(bron: () => Simulatiestand): void {
        this.snapshotbron = bron;
    }

    /**
     * De argumenten die bij elk gelogd element meegaan: de conditieset waarin het staat, en bij een
     * relatie de routes van zijn twee argumenten.
     */
    private elementargumenten(node: NodeSingular, args?: Record<string, unknown>): Record<string, unknown> {
        const argumenten: Record<string, unknown> = {...(args || {})};
        const type = node.data('type') as string;
        if (elements.isSubtypeOf(type, 'relation')) {
            argumenten['_fromroute'] = this.dlModel.route(this.dlModel.getElement(node.data('from') as string));
            argumenten['_toroute'] = this.dlModel.route(this.dlModel.getElement(node.data('to') as string));
        }
        argumenten['condtag'] = node.data('condtag');
        argumenten['condstate'] = node.data('condstate');
        return argumenten;
    }

    /**
     * Legt de stand van dit moment vast en stuurt hem naar het backend: het document, de
     * simulatiestand, de normstand, de bouwsuggesties en de aanwijzing. Alles tussen het lezen en het
     * versturen is synchroon. Het serialiseren is daarbij de diepe kopie: `modelData()` levert de
     * node-data per referentie aan, en pas als json-string ligt de stand echt vast.
     *
     * Een model dat nog nooit is opgeslagen krijgt geen snapshot: de replay begint bij de eerste save.
     */
    private async maakSnapshot(): Promise<void> {
        this.snapshotNodig = false;
        const modelId = this._modelId();
        if (!modelId || !this.tekenaar) {
            return; //zonder canvas valt er niets vast te leggen; wie terugkomt, laadt het model opnieuw
        }
        //wat van deze client is en niet van het model, gaat er niet of gelijkgetrokken in. Anders is dezelfde
        //stand bij twee deelnemers, of vóór en na een herlading, een andere snapshot en houdt geen
        //vergelijking hem tegen (test 3, 6 en 7 van test-golf-a5.md). De zoom (`cy`) gaat er helemaal uit:
        //de replay laat de uitsnede staan en leest hem nooit
        const {cy: _uitsnede, ...stand} = this.dlModel.modelData();
        const snapshot: Modelsnapshot = stand;
        //de selectie van cytoscape gaat er gelijkgetrokken in; het tekenen zet die toch altijd uit
        //(`ModelSerialisatie.lees`)
        for (const grafisch of Object.values(snapshot.nodes)) {
            grafisch['selected'] = false;
        }
        //`bs_data` gaat eruit: de stand van de bouwsuggestie-ballon op deze client (open of dicht, de
        //getoonde regel en de tellers), die bovendien achter kan lopen op de run. De replay haalt de lampjes
        //uit het bouwsuggestieblok. De elementdata komt per referentie van de nodes, dus een kopie zonder
        for (const [id, element] of Object.entries(snapshot.model.elements)) {
            const {bs_data: _ballonstand, ...zonderBallonstand} = element;
            snapshot.model.elements[id] = zonderBallonstand;
        }
        const simulatiestand = this.snapshotbron?.();
        if (simulatiestand) {
            snapshot.simstatus = simulatiestand.simstatus;
        }
        snapshot.norm = this.normService.snapshotNormstand();
        //en `isNieuw` gaat er gelijkgetrokken in: het hangt af van de run ervoor, en de replay leest het
        //niet, want die haalt elke stap terug als niet-nieuw
        snapshot.bouwsuggesties = this.bouwsuggestieEngine.exporteerRun()
            .map(node => ({...node, regels: node.regels.map(regel => ({...regel, isNieuw: false}))}));
        //wat er aangewezen stond hoort bij wat er te zien was. De simulatieselectie zit al in
        //`simstatus.simfeedback`
        const selectie = this.feedbackSelectie.selectie();
        snapshot.selectie = {fout: selectie.fout, bs: selectie.bs};
        const json = JSON.stringify(snapshot);
        //de weergavestand hoort bij de vergelijking: het backend legt hem als meta naast de json
        const inhoud = `${json}|${JSON.stringify([this.dlModel.modelleerniveau,
            this.dlModel.simulatiepreferentie, this.dlModel.fastest_path])}`;
        const vorige = this.laatsteSnapshot;
        if (vorige?.modelId === modelId && vorige.inhoud === inhoud && vorige.teller === this.snapshotteller) {
            this.log.debug('snapshot gelijk aan onze vorige, en er schreef intussen niemand: niet verstuurd');
            return;
        }
        //het volgnummer blijft buiten de json: het backend vergelijkt een stand op inhoud met de vorige,
        //en een oplopend veld erin zou elke vergelijking kapotmaken
        const volgorde = ++this.snapshotteller;
        this.laatsteSnapshotmoment = Date.now();
        this.snapshotsOnderweg++;
        try {
            const antwoord = await this.api.schrijfSnapshot(modelId, json, volgorde, simulatiestand?.simhash ?? null);
            //het backend beslist over het nummer. Is er intussen een ander model geladen, dan hoort dat
            //nummer niet bij onze teller
            if (this._modelId() === modelId) {
                this.zetSnapshotteller(antwoord?.volgorde);
                this.laatsteSnapshot = {modelId, inhoud, teller: this.snapshotteller};
            }
        } catch (fout) {
            this.log.warn('schrijfSnapshot mislukt', fout);
        } finally {
            this.snapshotsOnderweg--;
        }
    }

    /* ***************** OPSLAAN ***************** */

    /**
     * Slaat de meegegeven stand op in het model en plant het opslaan in. Dit is ook het moment
     * waarop de tellingen bijgewerkt worden.
     *
     * @param doc de nieuwe stand, in het opslagformaat
     * @param relevant of het om een inhoudelijke wijziging gaat
     * @param [direct] opslaan zonder vertraging
     * @param [saveNew] ook opslaan als het model nog nooit is opgeslagen (wordt anders overgeslagen)
     * @returns bij `direct` de uitkomst van de save, en anders `true` zodra het gepland is. Nooit
     *   een reject.
     */
    public save(doc: ModelDocument, relevant: boolean, direct?: boolean, saveNew?: boolean): Promise<boolean | string> {
        if (this.opslagGeblokkeerd) {
            //de graaf wordt op dit moment uit een document opgebouwd; dat is geen gebruikerswijziging
            return Promise.resolve(false);
        }
        if (this.dlModel.fullReadonly) {
            this.log.debug('save: geskipt wegens fullReadonly');
            return Promise.resolve(false);
        }
        if (this.samenwerking.isVolger()) {
            this.log.debug('save: geskipt want volger, maar wel tellen');
            return Promise.resolve(true);
        }
        this.annuleerGeplandeSave();

        //we kijken niet of de data gewijzigd is: de kans daarop is klein, en soms zetten we de
        //stand zelf en slaan we daarna op
        this.stand.zet(doc);
        this.addMeta();
        if (relevant) {
            this.verwerkNormstand();
        }

        if (!saveNew && !this.isOpgeslagen() && !this.magNieuwModelOpslaan(doc)) {
            return Promise.resolve(true);
        }

        if (direct) {
            return this.saveModel();
        }
        this.savePlanner = setTimeout(() => {
            this.savePlanner = null;
            if (this.samenwerking.isVolger()) {
                this.log.debug('geplande save vervalt: we volgen inmiddels');
                return;
            }
            void this.saveModel();
        }, SAVE_VERTRAGING);
        return Promise.resolve(true); //we wachten niet op het resultaat van een geplande save
    }

    /**
     * Slaat de huidige stand daadwerkelijk op. Levert `true`/`false` of, als er ondertussen een
     * ander model geladen is, de melding `'Oude generatie'` — nooit een reject. Let op: slaat ook op
     * als het model nog nooit is opgeslagen; dat hoort de aanroeper te bepalen.
     * @param [metPlaatje] stuur na een geslaagde save ook een vers voorbeeldplaatje
     */
    public async saveModel(metPlaatje = true): Promise<boolean | string> {
        //dezelfde guard die `save` al had. Zonder deze schrijft een al geplande save tijdens een
        //replay alsnog weg — met een voorbeeldplaatje van het teruggebladerde canvas, een versiebump
        //en een `modelchanged` naar de volgers. Bovenaan, want de wachtlus hieronder keert recursief
        //naar deze regel terug
        if (this.dlModel.fullReadonly) {
            this.log.debug('saveModel: geskipt wegens fullReadonly');
            return false;
        }
        if (this.saveBezig) {
            this.log.debug('saveModel uitgesteld, want nog bezig');
            await this.onNotSaving();
            return this.saveModel(metPlaatje);
        }
        if (this.samenwerking.isVolger()) {
            return false;
        }
        if (this.saveGeneratie === this.generatie) {
            return false; //deze generatie is al onderweg
        }

        let saveKlaar: () => void = () => {};
        this.saveBezig = new Promise<boolean>(resolve => {
            saveKlaar = () => resolve(true);
        });
        this.addMeta();
        let json: string;
        try {
            json = JSON.stringify(this.stand.ruweData());
        } catch (e) {
            this.log.error('saveModel: de stand is niet te serialiseren', e);
            this.saveBezig = null;
            saveKlaar();
            return false;
        }

        const dezeSave = this.saveGeneratie = ++this.generatie;
        //welke regels er meegaan leggen we vast: bij succes halen we precies dát voorste stuk uit de
        //buffer, want regels die ondertussen bijkomen zijn nooit verstuurd
        const meegestuurd = this.actionbuffer();
        const opslaan: Record<string, unknown> = {
            id: this._modelId(),
            titel: this.dlModel.titel,
            json,
            modelleerniveau: this.dlModel.modelleerniveau,
            simulatiepreferentie: this.dlModel.simulatiepreferentie,
            fastest_path: this.dlModel.fastest_path,
            actionbuffer: meegestuurd,
            //nog nooit opgeslagen én nog de standaardnaam: laat het backend hem uniek maken
            uniqueTitel: !this._modelId() && this.dlModel.titel === this.standaardtitel,
            samenwerking: this.samenwerking.isLeider()
                ? {vanVersie: this._versie(), changes: this.samenwerkchanges}
                : {vanVersie: this._versie()}
        };
        this.samenwerkchanges = []; //meteen legen, want de rest zit niet in deze versie

        let savedInfo: {id: ModelId; titel?: string; userprefix?: string; versie: number;
            snapshotvolgorde?: number; snapshotleeftijd?: number | null} | false;
        try {
            savedInfo = await this.api.saveModel(opslaan);
        } catch (fout) {
            this.log.debug('*** Opslaan reject', fout);
            const opnieuw = await this.meldSaveFout(fout as {code?: string});
            this.saveGeneratie = 0;
            this.saveBezig = null;
            saveKlaar();
            //het model is losgemaakt van zijn opgeslagen versie: dezelfde stand gaat nu als nieuw
            //model de deur uit. `_modelId()` is dan leeg, dus een tweede `NOT_FOUND` kan niet
            return opnieuw ? this.saveModel(metPlaatje) : false;
        }
        this.log.debug('*** Opslaan gelukt', savedInfo);
        this.saveGeneratie = 0;
        this.saveBezig = null;
        if (this.generatie !== dezeSave) {
            this.log.debug('Oude generatie, we stoppen na opslaan');
            saveKlaar();
            return 'Oude generatie';
        }
        if (!savedInfo) {
            return false;
        }

        const wasNieuw = !this._modelId();
        this._modelId.set(savedInfo.id);
        this.dlModel.titel = savedInfo.titel || this.dlModel.titel; //kan uniek gemaakt zijn
        this._userprefix.set(savedInfo.userprefix || this._userprefix());
        this._versie.set(savedInfo.versie);
        this.addMeta();
        //de stand van de snapshotreeks zoals het backend hem nu kent
        this.zetSnapshotteller(savedInfo.snapshotvolgorde);
        this.zetSnapshotleeftijd(savedInfo.snapshotleeftijd);
        //alleen het voorste stuk eruit: wat er tijdens de vlucht bij kwam is nooit verstuurd
        this.actielogbuffer.splice(0, meegestuurd.length);
        if (wasNieuw) {
            //eerste keer opslaan: de samenwerkregistratie moet staan (ook voor meekijken) en de
            //normtelling erbij, voordat we verder gaan
            if (!this._notifyId()) {
                this._notifyId.set(savedInfo.id);
            }
            await Promise.all(this.eersteOpslagMelding.meld());
            await this.stuurNormTelling();
            //de replay begint bij de eerste save: de stand van nu is de eerste stap
            this.markeerSnapshotNodig();
        }
        if (metPlaatje) {
            await this.stuurVoorbeeldplaatje();
        }
        saveKlaar();
        return true;
    }

    /**
     * Meldt een mislukte opslag aan de gebruiker en bepaalt of er een tweede poging volgt.
     *
     * Twee gevallen, want ze vragen iets heel anders van de gebruiker. Kent het backend het model
     * niet meer (`NOT_FOUND`) — iemand heeft het verwijderd, of het is nooit van deze gebruiker
     * geweest — dan is er niets meer om naar te schrijven, maar staat het werk nog wél op het
     * canvas. Dan is de vraag zinvol: als nieuw model bewaren? Zegt de gebruiker ja, dan maakt
     * `ontkoppel()` er een nog niet opgeslagen model van en gaat dezelfde stand alsnog de deur uit.
     *
     * In alle andere gevallen valt er niets zinnigs te vragen: een save komt net zo goed uit een
     * gewone bewerking als uit een klik op opslaan, en de gebruiker kan aan de melding niet zien
     * welke van de twee het was. Dan blijft het bij een melding die zegt wat hij eraan kan doen.
     *
     * Zonder verbinding melden we niets: dat lost zichzelf op zodra de lijn terug is.
     * @returns `true` als de save opnieuw geprobeerd moet worden
     */
    private async meldSaveFout(fout: {code?: string} | null): Promise<boolean> {
        if (!this.api.verbonden) {
            this.log.error('Save mislukt want geen verbinding - geen foutmelding');
            return false;
        }
        if (fout?.code === 'NOT_FOUND' && this._modelId()) {
            if (!await this.foutmelding.vraag('ERROR.SAVE_MODEL_WEG')) {
                return false;
            }
            this.ontkoppel();
            return true;
        }
        await this.foutmelding.toonTekst(this.translate.instant('ERROR.SAVE_MISLUKT'));
        return false;
    }

    /**
     * Geeft een promise die resolvet zodra we niet meer op een antwoord van het backend wachten.
     */
    public onNotSaving(): Promise<boolean> {
        return this.saveBezig ?? Promise.resolve(true);
    }

    /**
     * Of een nog nooit opgeslagen model nu al naar het backend mag, zonder dat de gebruiker daar
     * zelf om vroeg. Een leeg model niet, en onder de ballongrens ook niet: dan krijgt hij hoogstens
     * een vriendelijke herinnering om zelf op te slaan. Wordt het model zó groot dat er echt werk
     * verloren zou gaan, dan gaat het alsnog de deur uit — mét de nadrukkelijke ballon.
     */
    private magNieuwModelOpslaan(doc: ModelDocument): boolean {
        if (Object.keys(doc.model.elements).length === 0) {
            return false; //een leeg model, er is niets te doen
        }
        if (Object.keys(this.stand.ruweData().nodes).length < BALLONGRENS) {
            this.log.debug('save: geskipt want niet eerder opgeslagen');
            if (!this.opslaanballonGetoond) {
                this.toonOpslagballon('MODEL_WAARSCHUWOPSLAAN');
                this.opslaanballonGetoond = true; //maar één keer
            }
            return false;
        }
        this.toonOpslagballon('MODEL_WEGAANOPSLAAN');
        return true;
    }

    /**
     * Toont een beginnersuitleg-ballon bij de opslaanknop.
     */
    private toonOpslagballon(vertaalsleutel: string): void {
        const knop = document.getElementById('saveGraphbtn');
        if (knop) {
            BeginneruitlegDirective.toonOpElement(knop, vertaalsleutel);
        }
    }

    /**
     * Maakt een voorbeeldplaatje van de graaf en stuurt het naar het backend. Een tekenaar die het
     * niet redt houdt de save niet tegen.
     */
    private async stuurVoorbeeldplaatje(): Promise<void> {
        let plaatje = '';
        try {
            plaatje = this.graphJpg();
        } catch (e) {
            this.log.error('Model: het voorbeeldplaatje mislukte', e);
            return;
        }
        if (!plaatje) {
            return;
        }
        this.log.debug('*** Opslaan plaatje', Math.round(plaatje.length / 1024), 'kb');
        const id = this._modelId();
        if (id) {
            await this.api.saveModelImage(id, plaatje, this._versie());
        }
    }

    /**
     * Annuleert een geplande save.
     */
    private annuleerGeplandeSave(): void {
        if (this.savePlanner) {
            clearTimeout(this.savePlanner);
            this.savePlanner = null;
        }
    }

    /* ***************** DE MODELPROPERTIES ***************** */

    /**
     * Zet metaproperties van het model zonder op te slaan; alleen de meegegeven eigenschappen
     * veranderen. Meldt altijd, ook als er niets gewijzigd is, zodat de samenwerking zijn stappen
     * kan regelen.
     * @param props de te zetten eigenschappen
     * @param [notifyArg] vrij argument dat meegaat in de melding, om de flow te kunnen volgen
     */
    public setProperties(props: Modelproperties, notifyArg?: unknown): void {
        //dit mag ook in fullReadonly
        const gewijzigd: Modelproperties = {};
        if ('titel' in props) {
            this.dlModel.titel = props.titel as string;
            gewijzigd.titel = props.titel;
        }
        if ('modelleerniveau' in props) {
            this.dlModel.modelleerniveau = props.modelleerniveau as number;
            gewijzigd.modelleerniveau = props.modelleerniveau;
        }
        if ('simulatiepreferentie' in props) {
            this.dlModel.simulatiepreferentie = props.simulatiepreferentie as number;
            gewijzigd.simulatiepreferentie = props.simulatiepreferentie;
        }
        if ('fastest_path' in props) {
            this.dlModel.fastest_path = props.fastest_path as boolean | null;
            gewijzigd.fastest_path = props.fastest_path;
        }
        this.bepaalModelleerNiveau();
        this.propsGewijzigdMelding.meld({gewijzigd, notifyArg});
    }

    /**
     * Zet het effectieve modelleerniveau op grond van het modelleerniveau van het model en de
     * projectinstellingen. Blijft een expliciete methode op dezelfde vijf momenten als voorheen —
     * geen afleiding in `DlModel`, want dat zou het moment veranderen waarop de waarde bijkomt.
     */
    public bepaalModelleerNiveau(): void {
        const projectniveau = this.sessie.modelleerniveau();
        const overschrijfbaar = !!this.sessie.projectdata()?.['modelleerniveau_overschrijfbaar'];
        this.dlModel.effectief_modelleerniveau =
            (this.dlModel.modelleerniveau === 0 || !overschrijfbaar) ? projectniveau : this.dlModel.modelleerniveau;
        this.log.debug('effectief modelleerniveau', this.dlModel.effectief_modelleerniveau);
    }

    /**
     * Opent de eigenschappendialoog op dit model. Bij OK worden de ingestelde eigenschappen
     * teruggegeven, en — tenzij het om een kopie gaat — ook meteen toegepast; bij annuleren `null`.
     * Of er daarna opgeslagen moet worden bepaalt de aanroeper.
     * @param [aanhefLocale] vertaalsleutel voor de aanheftekst
     * @param [overwriteValues] eigenschappen die anders moeten zijn dan die van het model
     * @param [voorKopie] als `true` worden de gekozen eigenschappen niet toegepast, alleen teruggegeven
     */
    public propertiesDialog(
        aanhefLocale?: string, overwriteValues?: Modelproperties, voorKopie?: boolean
    ): Promise<ModelPropsKeuze> {
        if (this.dlModel.fullReadonly) {
            this.log.debug('propertiesDialog: geskipt wegens fullReadonly');
            return Promise.resolve(null);
        }
        const projectdata = this.sessie.projectdata();
        const initvalues = {
            aanhef: aanhefLocale || '',
            model: {
                titel: this.dlModel.titel,
                modelleerniveau: this.dlModel.modelleerniveau,
                simulatiepreferentie: this.dlModel.simulatiepreferentie,
                fastest_path: this.dlModel.fastest_path,
                ...overwriteValues
            },
            wijzigtitel: !!(voorKopie || this.samenwerking.rol() !== 'sub'),
            modelleerniveauOverschrijfbaar: !!projectdata?.['modelleerniveau_overschrijfbaar'],
            simulatiepreferentieOverschrijfbaar: !!projectdata?.['simulatiepreferentie_overschrijfbaar'],
            projectModelleerniveau: this.sessie.modelleerniveau() || 20,
            projectSimulatiepreferentie: projectdata?.['simulatiepreferentie'] ?? null,
            fastestPathOverschrijfbaar: this.sessie.fastestPathOverschrijfbaar(),
            projectFastestPath: !!projectdata?.['fastest_path']
        };
        return new Promise<ModelPropsKeuze>(resolve => {
            this.modalService.show(DlModelPropsComponent, {
                backdrop: 'static',
                animated: true,
                class: 'dl-modal',
                initialState: {
                    initvalues,
                    resolve: (keuze: ModelPropsKeuze) => {
                        if (keuze && !voorKopie) {
                            this.setProperties(keuze);
                        }
                        resolve(keuze);
                    }
                }
            });
        });
    }

    /* ***************** DE LAADKETEN ***************** */

    /**
     * Begint een leeg, nieuw model.
     * @param [stil] geen herlaadmelding: de aanroeper tekent zelf
     */
    public async nieuw(stil?: boolean): Promise<boolean> {
        this.reset();
        await this.samenwerkhaak?.(true); //deregistreren: dit model kent niemand
        if (!stil) {
            this.herlaad('nieuw model');
        }
        return true;
    }

    /**
     * Begint een nieuw model op basis van een sjabloon: de inhoud van het sjabloon wordt de
     * beginstand, maar het model is niet opgeslagen en heeft geen eigen id.
     * @param sjabloonId het model dat als sjabloon dient
     * @param [isAdminKey] `true` als het id een versleutelde toegangssleutel is
     */
    public async nieuwUitSjabloon(sjabloonId: ModelId, isAdminKey?: boolean): Promise<boolean> {
        this.reset();
        const loaddata = await this.api.getModelById(sjabloonId, isAdminKey);
        const doc = this.pakDocumentUit(loaddata);
        if (!doc) {
            return false;
        }
        //het modelleerniveau en de simulatievoorkeur komen uit het sjabloon; of ze mogen afwijken
        //van het project bepaalt de modelbouwcode
        this.neemModelinstellingenOver(loaddata);
        //een sjabloon kan ook een norm bevatten
        this.dlModel.norm = doc.meta?.norm || false;
        this.stand.neemOver(doc);
        this.addMeta();
        this.dlModel.werkNormnotatieBij();
        await this.samenwerkhaak?.(true);
        this.verwerkNormstand();
        this.herlaad('nieuw model');
        return true;
    }

    /**
     * Begint een nieuw, leeg model met het gegeven sjabloon als normmodel: het model zelf blijft
     * leeg, maar de normbalk weet wat er zou moeten komen.
     * @param sjabloonId het model dat als norm dient
     */
    public async nieuwMetNormmodel(sjabloonId: ModelId): Promise<boolean> {
        this.reset();
        const loaddata = await this.api.getModelById(sjabloonId);
        const doc = this.pakDocumentUit(loaddata);
        if (!doc) {
            return false;
        }
        this.neemModelinstellingenOver(loaddata);
        this.dlModel.bouwNormUitSjabloon(sjabloonId, loaddata.titel, doc);
        await this.samenwerkhaak?.(true);
        this.verwerkNormstand();
        this.herlaad('nieuw model');
        return true;
    }

    /**
     * Laadt een model uit het backend: alles gaat opnieuw, inclusief de samenwerkstand en de chat.
     * @param modelId het model, of een versleutelde toegangssleutel
     */
    public async laad(modelId: ModelId): Promise<void> {
        this.reset();
        let loaddata: Laadresultaat;
        try {
            loaddata = await this.api.getModelById(modelId);
        } catch (fout) {
            await this.foutmelding.toon(fout as {code?: string});
            return;
        }
        const doc = this.pakDocumentUit(loaddata);
        if (!doc) {
            return;
        }
        this._modelId.set(loaddata.id);
        this.dlModel.titel = loaddata.titel;
        this._userprefix.set(loaddata.userprefix || ''); //een gebruikersnaam als het model van een ander is
        this.neemModelinstellingenOver(loaddata);
        this.dlModel.norm = doc.meta?.norm || false;
        this.samenwerking.zetRolUitBackend(loaddata.samenwerking);
        this.samenwerking.zetMeekijkmodel(loaddata.meekijkmodel ?? null);
        this._versie.set(loaddata.versie || 0);
        //de stand van de snapshotreeks: onze eerste snapshot komt boven dat volgnummer, en niet eerder
        //dan de wachtdrempel na dat moment
        this.zetSnapshotteller(loaddata.snapshotvolgorde);
        this.zetSnapshotleeftijd(loaddata.snapshotleeftijd);
        this.stand.neemOver(doc);
        this.addMeta();
        this.dlModel.werkNormnotatieBij();
        this._notifyId.set(this.notifyIdUit(loaddata));
        this.geladenMelding.meld({chat: loaddata.chat ?? []});

        await this.samenwerkhaak?.();
        this.verwerkNormstand(); //nu is de werkmodus bekend
        this.herlaad('nieuw model');
    }

    /**
     * Haalt de modeldata opnieuw uit het backend zonder het model te verlaten: de zoom, de
     * undo-geschiedenis en de chat blijven zoals ze zijn. Vooral bij samenwerking, als er van de
     * ander iets binnenkwam dat we niet lokaal konden bijhouden.
     * @param [aanleiding] waarom er herladen wordt; gaat naar allebei de staarten, de normstand en de
     *   tekenmelding. Bij `herstel` — de terugkeer uit een replay — telt de verse uitslag daardoor
     *   niet als nieuws en pulseert er niets
     * @param [wisUndobuffer] gooi de undo-geschiedenis toch weg
     * @returns of er daadwerkelijk een stand geladen is
     */
    public async herlaadUitBackend(aanleiding: Tekenaanleiding = 'samenwerk-herlading',
                                   wisUndobuffer?: boolean): Promise<boolean> {
        if (this.dlModel.fullReadonly) {
            this.log.debug('herlaadUitBackend: geskipt wegens fullReadonly');
            return false;
        }
        const modelId = this._modelId();
        if (!modelId) {
            return false; //nog niets om te herladen
        }
        this.nieuweGeneratie(); //oude saves zijn ongeldig op dit model
        const loaddata = await this.api.getModelById(modelId);
        const doc = this.pakDocumentUit(loaddata);
        if (!doc) {
            return false;
        }
        this.dlModel.titel = loaddata.titel;
        this._userprefix.set(loaddata.userprefix || '');
        this.neemModelinstellingenOver(loaddata);
        this.dlModel.norm = doc.meta?.norm || false;
        this._versie.set(loaddata.versie || 0);
        //de stand van de snapshotreeks trekken we bij: er kan intussen een ander geschreven hebben, en
        //onze volgende snapshot hoort daarboven te liggen en de drempel vanaf de zijne aan te houden
        this.zetSnapshotteller(loaddata.snapshotvolgorde);
        this.zetSnapshotleeftijd(loaddata.snapshotleeftijd);
        this.stand.neemOver(doc);
        this.addMeta();
        this.leegActionbuffer(); //die klopt niet meer bij deze stand
        this.dlModel.werkNormnotatieBij();
        if (wisUndobuffer) {
            this.log.debug('[undo] Clear');
            this.wisGeschiedenis();
        }
        //zet de samenwerking opnieuw op
        this.samenwerking.zetRolUitBackend(loaddata.samenwerking);
        this._notifyId.set(this.notifyIdUit(loaddata));

        await this.samenwerkhaak?.();
        this.verwerkNormstand(aanleiding); //nu is de werkmodus bekend
        this.herlaad(aanleiding); //zelfde model, verse stand
        this.samenwerking.zetMeekijkmodel(loaddata.meekijkmodel ?? null);
        return true;
    }

    /**
     * Ontkoppelt het model van zijn opgeslagen versie: we behandelen wat er staat vanaf nu als een
     * nieuw, nog niet opgeslagen model.
     */
    public ontkoppel(): void {
        if (this.dlModel.fullReadonly) {
            this.log.debug('ontkoppel: geskipt wegens fullReadonly');
            return;
        }
        this.samenwerking.zetRolUitBackend('single');
        this.samenwerking.zetWerkmodus('lokaal');
        this.samenwerking.zetMeekijkmodel(null);
        this._userprefix.set('');
        this._modelId.set(null);
        this._notifyId.set(null);
        void this.samenwerkhaak?.(true);
        this.nieuweGeneratie(); //oude saves zijn ongeldig op dit model
    }

    /**
     * Meldt hoe de opslagketen een model bij de samenwerking aan- en afmeldt. Een registratie en
     * geen injectie: de samenwerk-baas meldt zich hier, en die injecteert deze service al.
     */
    public registreerSamenwerkhaak(haak: Samenwerkhaak): void {
        this.samenwerkhaak = haak;
    }

    /**
     * Er is een model geladen (ng1: het moment waarop de chat zijn historie kreeg). Geeft de
     * afmeldfunctie terug, voor een component dat opnieuw aangemaakt kan worden.
     */
    public bijGeladen(luisteraar: (geladen: Geladenmelding) => void): () => void {
        return this.geladenMelding.abonneer(luisteraar);
    }

    /**
     * Pakt het opslagformaat uit een backendantwoord uit. Geeft `null` als er niets geladen kon
     * worden; dan blijft staan wat er stond.
     */
    private pakDocumentUit(loaddata: Laadresultaat | null | undefined): ModelDocument | null {
        if (!loaddata) {
            return null;
        }
        try {
            return JSON.parse(loaddata.json) as ModelDocument;
        } catch (fout) {
            this.log.error('De opgeslagen modeldata is geen geldige json', fout);
            return null;
        }
    }

    /**
     * Neemt modelleerniveau, simulatievoorkeur en snelste-padgedrag over uit een backendantwoord.
     * Het snelste pad alleen als het project dat laat overschrijven.
     */
    private neemModelinstellingenOver(loaddata: Laadresultaat): void {
        this.dlModel.modelleerniveau = loaddata.modelleerniveau ?? 0;
        this.bepaalModelleerNiveau();
        this.dlModel.simulatiepreferentie = loaddata.simulatiepreferentie ?? 0;
        this.dlModel.fastest_path = this.sessie.fastestPathOverschrijfbaar() ? (loaddata.fastest_path ?? null) : null;
    }

    /**
     * Het model waarvan we notificaties willen. Een sub volgt de uitzendingen van zijn primary;
     * verder zijn we dat zelf — ook om te kunnen meekijken.
     */
    private notifyIdUit(loaddata: Laadresultaat): ModelId | null {
        return loaddata.samenwerking === 'sub' ? (loaddata.primary?.id ?? null) : (loaddata.id ?? null);
    }

    /* ***************** DE NORM-STAARTEN ***************** */

    /**
     * De stand is gewijzigd: laat de norm zijn telling en zo nodig zijn match bijwerken. Wat er ná
     * de norm gebeurt — loggen en versturen — hangt aan zijn meldingen, hieronder.
     */
    private verwerkNormstand(aanleiding?: Tekenaanleiding): void {
        this.normService.verwerkStand(this.stand.ruweData().model as unknown as WerkModelData, aanleiding);
    }

    /**
     * Laat de norm opnieuw matchen op de huidige stand. De samenwerk-baas roept dit aan bij een
     * wisseling van werkmodus, als een verse leider de normmatch nog niet heeft.
     */
    public maakNormmatch(): void {
        this.normService.maakNormmatch(this.stand.ruweData().model.elements as unknown as NormmatchElementen);
    }

    /**
     * Stuurt de normtelling naar het backend. Alleen bij een opgeslagen model, en niet als volger:
     * die rekent dezelfde telling uit en zou hem dubbel vastleggen.
     */
    public stuurNormTelling(): Promise<unknown> | void {
        if (this.dlModel.fullReadonly || !this.isOpgeslagen() || this.samenwerking.isVolger()) {
            return;
        }
        const norm = this.dlModel.norm;
        return this.api.saveNormTelling(this._modelId() as ModelId, this.normService.telling(), norm ? norm.teller : null);
    }

    /**
     * Koppelt de log- en save-staarten aan de meldingen van de norm. De norm meldt, wij loggen:
     * andersom zou de norm de opslagketen injecteren terwijl die hem al aanroept.
     */
    private luisterNaarDeNorm(): void {
        //de `help`-regel: de norm stelt hem samen bij elk vers foutoverzicht, wij versturen hem —
        //hier zitten het modelid en de snapshots (golf a4 stap 3, was ng1)
        this.normService.registreerLogHelp(payload =>
            this.directlogAction('help', payload as unknown as Record<string, unknown>, 'model', 'model'));

        //elke afloop van een rekenronde is een van de momenten om naar rust te kijken
        this.normService.bijNormronde(() => this.planRustcheck(0));

        this.normService.bijNormdata(melding => {
            //een volger krijgt deze normdata van zijn leider en zou hem dus dubbel vastleggen,
            //zonder dat er iets aan hem toe te schrijven valt; in readonly loggen we sowieso niet
            if (!melding.gewijzigdFoutmodel || this.dlModel.fullReadonly || this.samenwerking.isVolger()) {
                return;
            }
            this.directlogAction('errormodel', melding.gewijzigdFoutmodel as unknown as Record<string, unknown>);
        });

        this.normService.bijFouttelling(() => {
            if (this.dlModel.fullReadonly) {
                return;
            }
            void this.stuurNormTelling();
            //Loggen alleen als de uitkomst afwijkt van wat we het laatst logden. Bewust niet
            //vergeleken met de stand van vlak vóór deze aanroep: `berekenTelling` zet bij elke
            //modelwijziging de foutaantallen eerst op null, waarna ze hier weer ingevuld worden.
            //Dat null → getal-verschil is geen inhoudelijke wijziging, maar leverde wel bij elke
            //bewerking een loze errorcount-regel op — en meerdere per bewerking, omdat de keten per
            //wijziging vaker langskomt.
            const telling = this.normService.telling();
            if (this.samenwerking.isVolger() || JSON.stringify(this.laatstGelogdeTelling) === JSON.stringify(telling)) {
                return;
            }
            this.laatstGelogdeTelling = telling;
            const norm = this.dlModel.norm;
            this.directlogAction('errorcount', {telling, norm: norm ? norm.teller : null});
        });
    }

    /* ***************** HET TEKENEN ***************** */

    /**
     * Het canvas meldt zich als tekenaar van het document. Spiegelbeeld van
     * `DlModel.bijGraafGetekend`: de service kent het component niet, het component kent de service.
     */
    public registreerTekenaar(tekenaar: Modeltekenaar): void {
        this.tekenaar = tekenaar;
    }

    /**
     * Het canvas gaat weg (statewissel); wie er niet is, tekent niet.
     */
    public deregistreerTekenaar(tekenaar: Modeltekenaar): void {
        if (this.tekenaar === tekenaar) {
            this.tekenaar = null;
        }
    }

    /**
     * Het voorbeeldplaatje van het model, of een lege string als er geen canvas is.
     */
    public graphJpg(maxwidth?: number, maxheight?: number): string {
        return this.tekenaar?.jpg(maxwidth, maxheight) ?? '';
    }

    /* ***************** DE OPSLAGBLOKKADE ***************** */

    /**
     * Of het model op dit moment niet opgeslagen mag worden; zie `blokkeerOpslag`.
     */
    public get opslagGeblokkeerd(): boolean {
        return this.opslaglock > 0;
    }

    /**
     * Voert `fn` uit met het opslaan geblokkeerd: terwijl de graaf uit een document opnieuw wordt
     * opgebouwd is wat er op het canvas staat geen gebruikerswijziging, en er mag dus niets naar het
     * backend. De blokkade telt op, zodat geneste blokkades elkaar niet opheffen, en geldt alleen
     * voor de synchrone uitvoering van `fn` — net als de ng1-`lockSave` die dit ooit was.
     */
    public blokkeerOpslag<T>(fn: () => T): T {
        this.opslaglock++;
        try {
            return fn();
        } finally {
            this.opslaglock--;
        }
    }

    /* ***************** UNDO / REDO ***************** */

    /**
     * Of er ongedaan gemaakt kan worden. In `fullReadonly` nooit; een volger toont de stand van de
     * leider, want zijn undo is een verzoek aan die leider.
     */
    public readonly canUndo: Signal<boolean> = computed(() => {
        if (this.dlModel.fullReadonly) {
            return false;
        }
        return this.samenwerking.isVolger() ? this._leiderUndostatus().canUndo : this._standCanUndo();
    });

    /**
     * Of er opnieuw gedaan kan worden; zie `canUndo`.
     */
    public readonly canRedo: Signal<boolean> = computed(() => {
        if (this.dlModel.fullReadonly) {
            return false;
        }
        return this.samenwerking.isVolger() ? this._leiderUndostatus().canRedo : this._standCanRedo();
    });

    /**
     * Bewaart de huidige stand als undopunt. Aan te roepen vóór een logische wijziging, want het is
     * de stand waar je naartoe terug wilt: ná de wijziging wordt er opgeslagen en is de stand een
     * andere.
     */
    public setUndoPoint(): void {
        if (this.dlModel.fullReadonly) {
            this.log.debug('setUndoPoint: geskipt wegens fullReadonly');
            return;
        }
        this.stand.setUndoPoint();
        this.verversUndostatus();
    }

    /**
     * Maakt de laatste wijziging ongedaan: de vorige stand wordt de huidige en wordt getekend.
     * Opslaan regelt de aanroeper.
     */
    public undo(): void {
        if (this.dlModel.fullReadonly) {
            this.log.debug('undo: geskipt wegens fullReadonly');
            return;
        }
        if (this.samenwerking.isVolger() || !this.stand.undo()) {
            return; // een volger vraagt het zijn leider; en zonder stapel valt er niets te doen
        }
        this.log.debug('[undo] undo', this.stand.diepte());
        this.herlaad('herstel');
        this.verwerkNormstand('herstel');
        this.verversUndostatus();
    }

    /**
     * Zet een ongedaan gemaakte wijziging terug; zie `undo`.
     */
    public redo(): void {
        if (this.dlModel.fullReadonly) {
            this.log.debug('redo: geskipt wegens fullReadonly');
            return;
        }
        if (this.samenwerking.isVolger() || !this.stand.redo()) {
            return;
        }
        this.log.debug('[undo] redo', this.stand.diepte());
        this.herlaad('herstel');
        this.verwerkNormstand('herstel');
        this.verversUndostatus();
    }

    /**
     * Gooit de undo-geschiedenis weg: een ander of opnieuw geladen model heeft er niets aan. Meldt
     * bewust niets: het opnieuw beginnen is geen undo-gebaar, en de ng1-code stuurde hier ook geen
     * undostatus over de samenwerklijn. De knoppen komen wel bij: die lezen de signals.
     */
    public wisGeschiedenis(): void {
        this.stand.wisGeschiedenis();
        this.verversStandsignals();
    }

    /**
     * De leider meldt zijn undostatus; die tonen wij als volger op onze knoppen. Komt binnen via de
     * samenwerk-baas, uit de notify `undostatus`.
     */
    public zetLeiderUndostatus(status: Undostatus): void {
        this._leiderUndostatus.set({canUndo: !!status?.canUndo, canRedo: !!status?.canRedo});
    }

    /**
     * Het aantal bewaarde undo- en redostappen. Voor diagnose en voor het browserharnas, dat de
     * grens van `maxUndo` niet anders kan vaststellen.
     */
    public diepte(): {undo: number; redo: number} {
        return this.stand.diepte();
    }

    /* ***************** MELDINGEN ***************** */

    /**
     * De stand is vervangen en opnieuw getekend (ng1: `model.changed`).
     * @param luisteraar krijgt de aanleiding waarom er getekend werd
     */
    public bijHerladen(luisteraar: (aanleiding: Tekenaanleiding) => void): void {
        this.herladenMelding.abonneer(luisteraar);
    }

    /**
     * Er is een nieuw, leeg model begonnen (ng1: `model.reset`). Geeft de afmeldfunctie terug, voor een
     * component dat opnieuw aangemaakt kan worden.
     */
    public bijGereset(luisteraar: () => void): () => void {
        return this.geresetMelding.abonneer(luisteraar);
    }

    /**
     * Onze undostatus is gewijzigd.
     */
    public bijUndostatus(luisteraar: (status: Undostatus) => void): void {
        this.undostatusMelding.abonneer(luisteraar);
    }

    /**
     * De metaproperties van het model zijn gezet (ng1: `model.propsChanged`). Geeft de afmeldfunctie
     * terug, voor een component dat opnieuw aangemaakt kan worden.
     */
    public bijPropsGewijzigd(luisteraar: (wijziging: Propswijziging) => void): () => void {
        return this.propsGewijzigdMelding.abonneer(luisteraar);
    }

    /**
     * Het model is voor het eerst opgeslagen en heeft nu een id. De save wacht op wat de luisteraar
     * teruggeeft voordat hij het voorbeeldplaatje verstuurt.
     */
    public bijEersteOpslag(luisteraar: () => void | Promise<void>): void {
        this.eersteOpslagMelding.abonneer(luisteraar);
    }

    /**
     * Er is een inhoudelijke wijziging gelogd (ng1: `model.contentChanged`). Geeft de afmeldfunctie
     * terug, voor een component dat opnieuw aangemaakt kan worden.
     */
    public bijInhoudGewijzigd(luisteraar: () => void): () => void {
        return this.inhoudGewijzigdMelding.abonneer(luisteraar);
    }

    /**
     * Meldt een inhoudelijke wijziging die niet uit een logregel volgt. Een volger die een
     * uitvoering van de leider overneemt logt niet, maar zijn weergave hoort wel mee te komen.
     */
    public meldInhoudGewijzigd(): void {
        this.inhoudGewijzigdMelding.meld();
    }

    /**
     * Werkt de undo-signals bij en meldt de nieuwe stand aan wie hem verstuurt. De twee zijn
     * gesplitst omdat niet elke stapelwijziging een gebaar van de gebruiker is; zie
     * `wisGeschiedenis`.
     */
    private verversUndostatus(): void {
        this.verversStandsignals();
        this.undostatusMelding.meld({canUndo: this.canUndo(), canRedo: this.canRedo()});
    }

    /**
     * Werkt de undo-signals bij naar de stand van de stapels.
     */
    private verversStandsignals(): void {
        this._standCanUndo.set(this.stand.canUndo());
        this._standCanRedo.set(this.stand.canRedo());
    }
}
